import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/** Turn an axios error (or string) into a readable sentence. FastAPI 422s carry a list in `detail`. */
export const errorMessage = (error, fallback = 'Something went wrong') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.map((d) => d?.msg || String(d)).join('; ');
  if (!error.isAxiosError && !error.response && !error.request && error.msg) return error.msg;
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join('; ');
  if (error.isAxiosError && !error.response) return 'Could not reach the server. Check your connection.';
  return error.message || fallback;
};

/**
 * Inline "couldn't load" card with a Retry button. Render it instead of empty
 * states or zero values when a fetch fails, so a failure never reads as "no data".
 */
export const LoadError = ({ what = 'this data', error, onRetry, retrying = false, className = '', testId = 'load-error' }) => (
  <div
    role="alert"
    data-testid={testId}
    className={`flex flex-wrap items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 ${className}`}
  >
    <div className="flex items-start gap-2">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>
        <p className="font-medium">Couldn't load {what}.</p>
        {error && <p className="mt-0.5 text-red-700">{errorMessage(error)}</p>}
      </div>
    </div>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry} disabled={retrying} data-testid={`${testId}-retry`}>
        <RefreshCw className={`mr-1.5 h-4 w-4 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
        Retry
      </Button>
    )}
  </div>
);

export default LoadError;
